import EditOffOutlinedIcon from "@mui/icons-material/EditOffOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, ClickAwayListener, IconButton, InputAdornment, InputBase, Stack, SvgIconProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedValue } from "rooks";

import { CopyButton } from "new-components/buttons/CopyButton";

import { EndAdornment } from "./components/EndAdornment";
import { FloatingLabel } from "./components/FloatingLabel";
import { InputContainer } from "./components/InputContainer";
import { Menu } from "./components/Menu";
import { BASE_DARK_COLOR, VALUE_TYPE_TO_INPUT_MODE, VALUE_TYPE_TO_TYPE_ATTR } from "./constants";
import {
  CreateOptionFunction,
  LoadOptionsFunction,
  OptionType,
  OptionsPageResult,
  SelectionValue,
  TextFieldVariant,
  ValueType,
} from "./types";
import { getCursorStyle, getInputColors, getOptionData } from "./utils";

export interface TextFieldInputProps<OptionData, Params> {
  variant: TextFieldVariant;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  value?: string | number;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  id?: string;
  name?: string;
  rows?: number;
  valueType?: ValueType;
  lockMode?: boolean;
  selectedOption?: SelectionValue<OptionData>;
  loadOptions?: LoadOptionsFunction<OptionData, Params>;
  onCreateOption?: CreateOptionFunction<OptionData>;
  options?: OptionType<OptionData>[];
  onOptionSelect?: (option: OptionType<OptionData>) => void;
  characterLimit?: number;

  /**
   * Callback for when the selection changes in multiple selection mode.
   * Provides the complete new list of selected options.
   */
  onSelectionChange?: (selectedOptions: OptionType<OptionData>[]) => void;

  /**
   * @default false
   */
  copyable?: boolean;

  /**
   * @default false
   */
  multiple?: boolean;

  /**
   * Optional icon component to display at the end of the input.
   */
  endIcon?: React.ComponentType<SvgIconProps>;
  onEndIconClick?: () => void;
  onKeydown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: () => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
  backgroundColor?: string;
  "data-testid"?: string;
}

export function TextFieldInput<OptionData, Params>(props: TextFieldInputProps<OptionData, Params>) {
  const {
    variant,
    value: propValue = "",
    onChange,
    placeholder,
    disabled = false,
    error = false,
    helperText,
    label,
    autoFocus,
    id,
    name,
    valueType = "text",
    lockMode = false,
    selectedOption: propSelectedOption,
    loadOptions,
    onCreateOption,
    options: propOptions,
    onOptionSelect,
    onSelectionChange,
    rows,
    endIcon,
    onEndIconClick,
    copyable = false,
    multiple = false,
    onKeydown,
    onBlur,
    onFocus,
    inputProps,
    backgroundColor = "white",
    onClick,
    characterLimit,
    ...extraProps
  } = props;

  // State for lock mode
  const [isLocked, setIsLocked] = useState<boolean>(lockMode);
  const [pages, setPages] = useState<OptionsPageResult<OptionData, Params>[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  // Helper function to get the internal value based on selection type
  const getInternalValue = useCallback((): string => {
    if (propSelectedOption) {
      if (Array.isArray(propSelectedOption)) {
        // Multiple selection - join labels with comma
        return propSelectedOption.map((option) => getOptionData(option).label).join(", ");
      }
      // Single selection
      return getOptionData(propSelectedOption).label;
    }
    return String(propValue);
  }, [propSelectedOption, propValue]);

  // Helper function to get selected options array
  const getSelectedOptions = useCallback((): OptionType<OptionData>[] => {
    if (propSelectedOption) {
      if (Array.isArray(propSelectedOption)) {
        return propSelectedOption;
      }
      return [propSelectedOption];
    }
    return [];
  }, [propSelectedOption]);

  // State for the selected value, the visible input text, and the label associated with the selected value
  const internalValue = getInternalValue();
  const [value, setValue] = useState<string>(internalValue);
  const [inputValue, setInputValue] = useState<string>(internalValue);
  const [selectedLabel, setSelectedLabel] = useState<string>(internalValue);
  const [selectedOptions, setSelectedOptions] = useState<OptionType<OptionData>[]>(getSelectedOptions());

  useEffect(() => {
    const newInternalValue = getInternalValue();
    const newSelectedOptions = getSelectedOptions();
    setValue(newInternalValue);
    setInputValue(newInternalValue);
    setSelectedLabel(newInternalValue);
    setSelectedOptions(newSelectedOptions);
  }, [propSelectedOption, propValue, getInternalValue, getSelectedOptions]);

  const loadedOptions = pages.flatMap((page) => page.options);

  const frontendOptions =
    variant === "autofill" && propOptions
      ? propOptions.filter((option) => getOptionData(option).label.toLowerCase().includes(inputValue.toLowerCase()))
      : propOptions ?? [];

  // Determine the options to display based on variant and props
  const displayedOptions = loadOptions ? loadedOptions : frontendOptions;

  // For multiple selection, we need to check if any of the selected options match
  const optionValueForSelected =
    propSelectedOption && "value" in propSelectedOption ? propSelectedOption.value : propValue;
  const selectedOption = multiple
    ? null // In multiple mode, we don't have a single selected option for display purposes
    : displayedOptions.find((opt) => getOptionData(opt).value === optionValueForSelected) ?? null;

  const [debouncedInputValue] = useDebouncedValue(inputValue, 500);
  const [focused, setFocused] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [hovered, setHovered] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef(inputValue);

  const handleOptionsLoad = useCallback(
    async (searchText: string, lastPage?: OptionsPageResult<OptionData, Params>) => {
      if (!loadOptions) {
        return;
      }

      const isNewSearch = searchRef.current !== searchText;

      if (isNewSearch) {
        searchRef.current = searchText;
      }

      setIsLoadingOptions(true);

      try {
        const newOptionsPage = await loadOptions(searchText, lastPage);

        // Check if the search context is still valid after the async operation
        if (searchRef.current === searchText) {
          setPages((prevPages) => (lastPage ? [...prevPages, newOptionsPage!] : [newOptionsPage!]));
        }
      } catch (error) {
        console.error("Failed to load options:", error);
      } finally {
        // Only set loading to false if the search context is still valid
        if (searchRef.current === searchText) {
          setIsLoadingOptions(false);
        }
      }
    },
    [loadOptions]
  );

  const fetchNextPage = useCallback(async () => {
    const lastPage = pages.at(-1);
    const hasMorePages = lastPage?.hasNextPage ?? false;
    if (!hasMorePages || isFetchingNextPage) return;

    setIsFetchingNextPage(true);
    await handleOptionsLoad(searchRef.current, lastPage);
    setIsFetchingNextPage(false);
  }, [handleOptionsLoad, isFetchingNextPage, pages]);

  // Load options when input is focused or debounced value changes (if loadOptions is used)
  useEffect(() => {
    if (focused && loadOptions) {
      // For autofill, also open the menu when loading options on focus
      if (variant === "autofill") {
        setMenuOpen(true);
      }
      handleOptionsLoad(debouncedInputValue);
    }
    // Close menu if focus is lost and not creating a new option
    if (!focused && !isCreating) {
      setMenuOpen(false);
    }
  }, [debouncedInputValue, handleOptionsLoad, focused, loadOptions, variant, isCreating]);

  // Event handlers
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (effectiveDisabled) return;

    const newValue = e.target.value;
    const numericValue = Number(newValue);

    // Prevent update if non-numeric characters are entered for numeric type
    if (valueType === "numeric" && Number.isNaN(numericValue)) {
      return;
    }

    setInputValue(newValue);

    if (variant === "autofill") {
      setMenuOpen(true);
    } else if (variant !== "select") {
      // For standard/search, update the main value and trigger onChange
      setValue(newValue);
      onChange?.(e); // Pass original event
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (disabled || (lockMode && isLocked)) return;

    setFocused(true);
    onFocus?.(e);
  };

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      // Prevent the click from bubbling up to the container
      // This stops the container click handler from being triggered
      // which would immediately re-focus the input after clearing
      e.stopPropagation(); // Prevent container click/focus
      setValue("");
      setInputValue("");
      setSelectedLabel("");
      setSelectedOptions([]);
      // Trigger onChange with an empty value, simulating an input event
      onChange?.({ target: { name, value: "" } } as ChangeEvent<HTMLInputElement>);
      inputRef.current?.focus();
    },
    [onChange, inputRef, name]
  );

  const handleNewSelectedOptions = (newSelectedOptions: OptionType<OptionData>[]): void => {
    setSelectedOptions(newSelectedOptions);

    // Update display value
    const newDisplayValue = newSelectedOptions.map((opt) => getOptionData(opt).label).join(", ");
    setValue(newDisplayValue);
    setInputValue(newDisplayValue);
    setSelectedLabel(newDisplayValue);

    // Trigger onChange with the new array of selected options
    onChange?.({ target: { name, value: newDisplayValue } } as ChangeEvent<HTMLInputElement>);

    // Call onSelectionChange with the new list of selected options
    onSelectionChange?.(newSelectedOptions);
  };

  const handleOptionSelect = (option: OptionType<OptionData>): void => {
    const { value: optionValue, label: optionLabel } = getOptionData(option);
    onOptionSelect?.(option);

    if (multiple) {
      // Multiple selection mode
      const isAlreadySelected = selectedOptions.some((selected) => getOptionData(selected).value === optionValue);

      let newSelectedOptions: OptionType<OptionData>[];
      if (isAlreadySelected) {
        // Remove option if already selected
        newSelectedOptions = selectedOptions.filter((selected) => getOptionData(selected).value !== optionValue);
      } else {
        // Add option if not selected
        newSelectedOptions = [...selectedOptions, option];
      }

      handleNewSelectedOptions(newSelectedOptions);
    } else {
      // Single selection mode (existing logic)
      setValue(optionValue);
      setInputValue(optionLabel);
      setSelectedLabel(optionLabel);

      // Trigger onChange with the selected option's value
      onChange?.({ target: { name, value: optionValue } } as ChangeEvent<HTMLInputElement>);

      setMenuOpen(false);
      setFocused(false);
      onBlur?.();
      inputRef.current?.blur(); // Ensure blur happens after selection
    }
  };

  const handleBlur = () => {
    // If the input is not focused then avoid calling functions of closing
    if (!focused) {
      return;
    }

    // For autofill, reset input to selected label if user typed something else and didn't select
    if (variant === "autofill") {
      if (value && inputValue !== selectedLabel) {
        setInputValue(selectedLabel);
      } else if (!value && inputValue) {
        // Clear input if nothing is selected and user typed something
        setInputValue("");
      }
    }

    setMenuOpen(false);
    setFocused(false);
    setHovered(false);
    setIsCreating(false);
    onBlur?.();
  };

  const handleClickAway = (event: MouseEvent | TouchEvent): void => {
    if (inputRef.current?.contains(event.target as Node)) {
      return;
    }
    if (menuRef.current?.contains(event.target as Node)) {
      return;
    }

    handleBlur();
  };

  const handleContainerClick: React.MouseEventHandler<HTMLDivElement> = (event): void => {
    if (effectiveDisabled) return;

    onClick?.(event);

    if (variant === "select") {
      setMenuOpen((prev) => !prev);
      setFocused(true);
      return;
    }

    if (variant === "autofill") {
      setMenuOpen((prev) => !prev);
      inputRef.current?.focus();
      return;
    }

    inputRef.current?.focus();
  };

  const toggleLockState = (e: React.MouseEvent<HTMLButtonElement>): void => {
    // Prevent the click from bubbling up to parent elements (InputContainer)
    // Without this, clicking the lock icon would also trigger the container's onClick
    // which would attempt to focus the input field, creating conflicting behavior
    e.stopPropagation();
    setIsLocked((previousState) => !previousState);
  };

  const handleCreateOption = async (newOptionLabel: string): Promise<OptionType<OptionData> | undefined> => {
    const trimmedLabel = newOptionLabel.trim();
    return onCreateOption && trimmedLabel ? onCreateOption(trimmedLabel) : undefined;
  };

  // Calculate the effective disabled state based on both the disabled prop and lock status
  const effectiveDisabled = disabled || (lockMode && isLocked);

  // Change cursor based on variant and disabled state
  const cursor = getCursorStyle(effectiveDisabled, variant);

  const theme = useTheme();

  const { inputBaseColor, inputOverlayColor } = getInputColors({
    effectiveDisabled,
    error,
    hovered,
  });

  const hasValue = inputValue !== "";
  let displayedValue: string;
  if (variant === "select") {
    if (multiple) {
      displayedValue = selectedOptions.map((opt) => getOptionData(opt).label).join(", ");
    } else {
      displayedValue = selectedOption?.label ?? "";
    }
  } else {
    displayedValue = inputValue;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box id={id} position="relative" sx={{ width: "100%", flexShrink: 0 }} {...extraProps}>
        <Stack direction="row" alignItems="center" gap="10px">
          <InputContainer
            ref={containerRef}
            focused={focused}
            error={error}
            onClick={handleContainerClick}
            cursor={cursor}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{ flex: 1 }}
            borderColor={inputOverlayColor}
            backgroundColor={backgroundColor}
          >
            {label && (
              <FloatingLabel
                htmlFor={id}
                focused={focused}
                hasValue={hasValue}
                variant={variant}
                color={inputBaseColor}
                backgroundColor={backgroundColor}
              >
                {label}
              </FloatingLabel>
            )}
            <InputBase
              inputRef={inputRef}
              value={displayedValue}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={label ? undefined : placeholder} // Don't show placeholder if label exists
              fullWidth
              onKeyDown={onKeydown}
              onMouseDown={(event) => {
                if (variant === "select" || variant === "autofill") {
                  // do not focus input to type on select variant since
                  // we don't type in this variant
                  event.preventDefault();
                }
              }}
              startAdornment={
                variant === "search" ? (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{
                        color: inputBaseColor,
                        transition: theme.transitions.create(["color"]),
                        width: "24px",
                        height: "24px",
                      }}
                    />
                  </InputAdornment>
                ) : null
              }
              endAdornment={
                <InputAdornment position="end" data-testid="end-adorment">
                  <EndAdornment
                    variant={variant}
                    value={value}
                    disabled={effectiveDisabled}
                    handleClear={handleClear}
                    menuOpen={menuOpen}
                    cursor={cursor}
                    inputBaseColor={inputBaseColor}
                    inputOverlayColor={inputOverlayColor}
                    endIcon={endIcon}
                    onEndIconClick={onEndIconClick}
                  />
                </InputAdornment>
              }
              readOnly={variant === "select"}
              disabled={effectiveDisabled}
              error={error}
              autoFocus={autoFocus}
              name={name}
              type={VALUE_TYPE_TO_TYPE_ATTR[valueType]}
              inputProps={{
                inputMode: VALUE_TYPE_TO_INPUT_MODE[valueType],
                "aria-label": label,
                ...inputProps,
              }}
              multiline={rows !== undefined}
              rows={rows}
              sx={{
                paddingY: "9.5px",
                "& .MuiInputBase-input": {
                  padding: 0,
                  cursor,
                  color: inputBaseColor,
                  ...(variant === "select" && { cursor: "pointer" }),
                },
              }}
            />
          </InputContainer>

          {lockMode && (
            <IconButton
              aria-label={isLocked ? "Enable editing" : "Disable editing"}
              onClick={toggleLockState}
              size="small"
            >
              {isLocked ? (
                <EditOutlinedIcon fontSize="small" sx={{ color: BASE_DARK_COLOR, width: "24px", height: "24px" }} />
              ) : (
                <EditOffOutlinedIcon fontSize="small" sx={{ color: BASE_DARK_COLOR, width: "24px", height: "24px" }} />
              )}
            </IconButton>
          )}

          {copyable && <CopyButton valueToCopy={displayedValue} />}
        </Stack>

        <Menu
          ref={menuRef}
          selectedValue={selectedOption}
          selectedValues={selectedOptions}
          multiple={multiple}
          menuOpen={menuOpen}
          options={displayedOptions}
          handleOptionSelect={handleOptionSelect}
          containerRef={containerRef}
          isLoadingOptions={isLoadingOptions}
          isFetchingNextPage={isFetchingNextPage}
          hasMorePages={pages.at(-1)?.hasNextPage ?? false}
          fetchNextPage={fetchNextPage}
          onCreateOption={onCreateOption ? handleCreateOption : undefined}
          isCreating={isCreating}
          setIsCreating={setIsCreating}
          handleNewSelectedOptions={handleNewSelectedOptions}
        />

        {(helperText || characterLimit) && (
          <Box
            sx={{
              mt: "5px",
              ml: "16px",
              fontSize: "12px",
              color: inputOverlayColor,
              fontFamily: "Barlow",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>{helperText}</Box>

            {characterLimit && (
              <Box>
                {internalValue.length} / {characterLimit}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}
